/**
 * RequireAuth
 *
 * Route wrapper for end-user pages (e.g. /account). Shows a spinner while the
 * session probe is in flight, a sign-in / sign-up prompt when logged out, an
 * app-side "verify your email" gate for any unverified session (defence in
 * depth — the backend already refuses to establish one), and the child UI once
 * authenticated and verified.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { LogIn, UserPlus, Loader2, UserCircle, MailWarning } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAppSettings } from '../context/AppSettingsContext';
import { getRetailerAccountsEnabled } from '../data/productTable';

interface Props {
  children: React.ReactNode;
}

/**
 * i18n keys for the `?auth_error=` reasons the OIDC callback can bounce back
 * with. Unknown reasons fall through to the generic "didn't complete" copy —
 * Keycloak can hand us a code we've never seen, and a vague translated message
 * beats a raw reason slug.
 */
const AUTH_ERROR_KEYS: Record<string, string> = {
  email_unverified: 'auth:callbackErrors.emailUnverified',
  access_denied: 'auth:callbackErrors.accessDenied',
  token_exchange_failed: 'auth:callbackErrors.incomplete',
  verification_failed: 'auth:callbackErrors.incomplete',
  user_save_failed: 'auth:callbackErrors.userSaveFailed',
  staff_requires_microsoft: 'auth:callbackErrors.staffRequiresMicrosoft',
};

/** The `?auth_error=` reason in the URL, if any. */
function readAuthErrorKey(): string | null {
  if (typeof window === 'undefined') return null;
  const reason = new URLSearchParams(window.location.search).get('auth_error');
  if (!reason) return null;
  return AUTH_ERROR_KEYS[reason] ?? 'auth:callbackErrors.incomplete';
}

export const RequireAuth: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation('auth');
  const { isAuthenticated, isChecking, user, login, signup } = useAuth();
  const { settings } = useAppSettings();

  // Accounts system off — globally, or suppressed for this retailer's portal →
  // there are no user accounts here; send them to the app home rather than
  // showing a sign-in prompt for a system that isn't live.
  if (!settings.accountsEnabled || !getRetailerAccountsEnabled()) {
    return <Navigate to="/" replace />;
  }

  if (isChecking) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <Loader2 size={24} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  // App-side email-verification gate. The BFF callback never creates a session
  // for an unverified address, so this is belt-and-suspenders — but it means an
  // unverified session can never render protected content even if one existed.
  if (isAuthenticated && user && !user.emailVerified) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <div className="card shadow-sm" style={{ maxWidth: 420, width: '100%' }}>
          <div className="card-body p-4 text-center">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-2"
              style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,193,7,0.12)' }}
            >
              <MailWarning size={24} className="text-warning" />
            </div>
            <h4 className="fw-bold mb-1">{t('verify.title')}</h4>
            <p className="text-muted small mb-0">
              {t('verify.body', { email: user.email || t('verify.yourEmail') })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) return <>{children}</>;

  const authErrorKey = readAuthErrorKey();

  return (
    <div className="d-flex align-items-center justify-content-center py-5">
      <div className="card shadow-sm" style={{ maxWidth: 420, width: '100%' }}>
        <div className="card-body p-4 text-center">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-2"
            style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(13,110,253,0.08)' }}
          >
            <UserCircle size={24} className="text-primary" />
          </div>
          <h4 className="fw-bold mb-1">{t('required.title')}</h4>
          <p className="text-muted small mb-3">{t('required.body')}</p>

          {authErrorKey && <div className="alert alert-warning small text-start">{t(authErrorKey)}</div>}

          <button
            type="button"
            className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
            onClick={() => login(window.location.pathname + window.location.search)}
          >
            <LogIn size={16} /> {t('titles.login')}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary w-100 mt-2 d-inline-flex align-items-center justify-content-center gap-2"
            onClick={() => signup()}
          >
            <UserPlus size={16} /> {t('login.createAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequireAuth;
